import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, Clock, BookOpen, Calendar, ChevronRight, Tag } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import TableOfContents from '../../components/academy/TableOfContents';
import FAQSection from '../../components/academy/FAQSection';
import ArticleCard from '../../components/academy/ArticleCard';
import Breadcrumbs from '../../components/academy/Breadcrumbs';

const API = process.env.REACT_APP_BACKEND_URL;

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(`${API}/api/academy/articles/by-slug/${slug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setArticle(data);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#115E59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Article Not Found</h1>
          <p className="text-slate-500 mb-6">The article you are looking for does not exist or has been removed.</p>
          <Link to="/academy" className="text-[#115E59] font-semibold hover:underline">Return to Academy</Link>
        </div>
      </div>
    );
  }

  const readTime = Math.max(1, Math.round((article.word_count || 0) / 250));
  const publishedDate = article.published_date ? new Date(article.published_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const categoryName = article.category ? article.category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
  const relatedArticles = article.related_articles_data || [];

  // Schema.org Article structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.excerpt,
    "datePublished": article.published_date,
    "dateModified": article.updated_date,
    "publisher": {
      "@type": "Organization",
      "name": "Quidity",
      "url": "https://quidity.fund"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://quidity.fund/academy/${article.slug}`
    }
  };

  // FAQ Schema
  const faqSchema = article.faqs && article.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="bg-[#FAFAFA]">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

      {/* Article Header */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-8 pt-8 pb-10">
          <Breadcrumbs category={article.category} categoryName={categoryName} articleTitle={article.title} />

          <div className="flex flex-wrap items-center gap-3 mb-5 mt-6">
            <Link
              to={`/academy/category/${article.category}`}
              data-testid="article-category-link"
              className="inline-flex items-center gap-1.5 bg-[#115E59]/10 text-[#115E59] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full hover:bg-[#115E59]/20 transition-colors"
            >
              <Tag className="h-3 w-3" />
              {categoryName}
            </Link>
            {article.is_cornerstone && (
              <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                Comprehensive Guide
              </span>
            )}
          </div>

          <h1
            data-testid="article-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.1] mb-5"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {article.title}
          </h1>

          <p className="text-sm text-slate-400 mb-5">By Dr. Aaron Alonzo, PhD</p>

          <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-6 max-w-3xl">{article.excerpt}</p>

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
            {publishedDate && (
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {publishedDate}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {readTime} min read
            </span>
            <span className="flex items-center gap-1.5">
              <BookOpen className="h-4 w-4" />
              {(article.word_count || 0).toLocaleString()} words
            </span>
          </div>
        </div>
      </section>

      {/* Article Body + Sidebar */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* TOC Sidebar */}
          <aside className="lg:col-span-3 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28">
              {article.headings && article.headings.length > 0 && (
                <TableOfContents headings={article.headings} />
              )}
              {/* Quick CTA */}
              <div className="mt-6 bg-[#115E59] rounded-xl p-6 text-white">
                <h4 className="text-sm font-bold mb-2">Need Financing?</h4>
                <p className="text-xs text-teal-100/80 mb-4">Quidity provides the solutions discussed in this article.</p>
                <Link
                  to="/apply"
                  data-testid="sidebar-apply-cta"
                  className="inline-flex items-center gap-1.5 bg-white text-[#115E59] text-xs font-bold px-4 py-2 rounded-full hover:bg-teal-50 transition-colors w-full justify-center"
                >
                  Apply Now <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <article className="lg:col-span-9 order-1 lg:order-2" data-testid="article-content">
            <div className="article-content bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeSlug]}
                components={{
                  h2: ({children, ...props}) => (
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-12 mb-4 pb-2 border-b border-slate-100" style={{ fontFamily: 'var(--font-heading)' }} {...props}>{children}</h2>
                  ),
                  h3: ({children, ...props}) => (
                    <h3 className="text-xl font-bold text-slate-800 mt-8 mb-3" style={{ fontFamily: 'var(--font-heading)' }} {...props}>{children}</h3>
                  ),
                  p: ({children}) => (
                    <p className="text-base text-slate-600 leading-relaxed mb-5">{children}</p>
                  ),
                  ul: ({children}) => (
                    <ul className="list-disc pl-6 mb-5 space-y-2">{children}</ul>
                  ),
                  ol: ({children}) => (
                    <ol className="list-decimal pl-6 mb-5 space-y-2">{children}</ol>
                  ),
                  li: ({children}) => (
                    <li className="text-base text-slate-600 leading-relaxed">{children}</li>
                  ),
                  strong: ({children}) => (
                    <strong className="font-bold text-slate-900">{children}</strong>
                  ),
                  blockquote: ({children}) => (
                    <blockquote className="border-l-4 border-[#115E59] pl-6 py-2 my-6 bg-teal-50/30 rounded-r-lg">{children}</blockquote>
                  ),
                  table: ({children}) => (
                    <div className="overflow-x-auto my-6 rounded-lg border border-slate-200">
                      <table className="w-full text-sm">{children}</table>
                    </div>
                  ),
                  thead: ({children}) => (
                    <thead className="bg-slate-50">{children}</thead>
                  ),
                  th: ({children}) => (
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">{children}</th>
                  ),
                  td: ({children}) => (
                    <td className="px-4 py-3 text-slate-600 border-b border-slate-100">{children}</td>
                  ),
                  a: ({href, children}) => (
                    <a href={href} className="text-[#115E59] font-semibold hover:underline">{children}</a>
                  ),
                  code: ({inline, children}) => inline ? (
                    <code className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-sm">{children}</code>
                  ) : (
                    <code className="block bg-slate-50 p-4 rounded-lg text-sm overflow-x-auto mb-5">{children}</code>
                  ),
                }}
              >
                {article.content}
              </ReactMarkdown>
            </div>

            {/* Structured FAQ (from parsed data) */}
            {article.faqs && article.faqs.length > 0 && (
              <div className="mt-10">
                <FAQSection faqs={article.faqs} />
              </div>
            )}

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2">
                {article.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/academy/search?q=${encodeURIComponent(tag)}`}
                    className="text-xs font-semibold bg-slate-100 text-slate-500 px-3 py-1.5 rounded-full hover:bg-slate-200 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Author Bio */}
            <div className="mt-10 border-t border-slate-200 pt-8" data-testid="author-bio">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">About the Author</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Dr. Aaron Alonzo, PhD is the Founder of Quidity and the author of Quidity Academy. His work focuses on commercial lending, SBA financing, commercial real estate, cash flow engineering, underwriting, business finance, financial statement analysis, and business capital strategy. Through Quidity Academy, he provides educational resources that help business owners understand how lenders evaluate businesses and make financing decisions.
              </p>
            </div>
          </article>
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.slice(0, 6).map(ra => (
                <ArticleCard key={ra.slug} article={ra} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-slate-950 text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            Explore More from Quidity Academy
          </h2>
          <p className="text-sm text-slate-400 mb-6">Continue learning about commercial finance, or apply what you have learned with Quidity financing solutions.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/academy" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-6 py-3 rounded-full hover:bg-slate-100 transition-all">
              Browse Academy <ChevronRight className="h-4 w-4" />
            </Link>
            <Link to="/apply" className="inline-flex items-center gap-2 bg-white/[0.07] border border-white/10 text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-white/[0.12] transition-all">
              Apply for Financing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticlePage;
